import { useCallback } from "react";
import { QueryClient } from "@tanstack/react-query";
import { Message, MessagesState } from "@/lib/core/crm/types";

export function useUtils(state: MessagesState, queryClient: QueryClient) {
  // Refresh all messages
  const refreshAllMessages = useCallback(async () => {
    // Invalidate all message queries in TanStack Query
    void queryClient.invalidateQueries({
      queryKey: ["messages"],
    });
  }, [queryClient]);

  // Get total unread count across all inboxes
  const getTotalUnreadCount = useCallback(() => {
    return Object.values(state.unreadCounts).reduce((total, providers) => {
      return total + Object.values(providers).reduce((sum, count) => sum + count, 0);
    }, 0);
  }, [state.unreadCounts]);

  // Get all messages from all inboxes (for unified view)
  const getAllMessages = useCallback(
    (filter: string = "all") => {
      const allMessages: Message[] = [];

      // Iterate through all inboxes and providers
      Object.entries(state.messagesByInbox).forEach(([inboxId, providers]) => {
        Object.entries(providers).forEach(([provider, messages]) => {
          if (messages && messages.length > 0) {
            allMessages.push(...messages);
          }
        });
      });

      // Apply filter
      let filteredMessages = allMessages;
      if (filter === "unread") {
        filteredMessages = allMessages.filter((msg) => !msg.isRead);
      } else if (filter === "important") {
        filteredMessages = allMessages.filter((msg) => msg.importance === "high");
      }

      // Sort by received date (newest first)
      return filteredMessages.sort(
        (a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
      );
    },
    [state.messagesByInbox]
  );

  return {
    refreshAllMessages,
    getTotalUnreadCount,
    getAllMessages,
  };
}
